import React from 'react';
import {Avatar, Space, Tooltip} from 'antd';
import {DEFAULT_PAGE_SIZE} from '../../constants';
import {textHelpers} from './text.helpers';

/**
 * Collection of helpers for table sorting, pagination, filtering, export, and rendering.
 * @example
 * import { tableHelpers } from './table.helpers';
 * const orderBy = tableHelpers.getOrderByValue(sorter);
 */
export const tableHelpers = {
  /**
   * Converts an Ant Design table sorter object into an API order-by string.
   * @param {Object} [sorter={}] - Ant Design sorter object with order and field properties.
   * @param {string} [sorter.order] - Sort direction: 'ascend' or 'descend'.
   * @param {string} [sorter.field] - Field name to sort by.
   * @returns {string|undefined} Order-by string like '-amount' for descending or 'amount' for ascending; undefined if no order.
   * @example
   * tableHelpers.getOrderByValue({ order: 'descend', field: 'amount' }); // '-amount'
   */
  getOrderByValue: (sorter = {}) => {
    const {order, field} = sorter;
    return order ? `${order === 'descend' ? '-' : ''}${field}` : undefined
  },
  /**
   * Handles pagination changes for a table, loading new data for unloaded pages or updating the local page state.
   * @param {Object} options - Pagination options.
   * @param {number} [options.activePage=1] - The page number the user navigated to.
   * @param {Function} options.loadData - Callback to fetch data for a new page; called with a query object.
   * @param {Function} options.setTableData - Callback to update local table state when the page is already loaded.
   * @param {Object} [options.tableData={}] - Current table state including page, loadedPages, and pagination.
   * @returns {void}
   * @example
   * tableHelpers.handleOnPaginate({ activePage: 2, loadData, setTableData, tableData });
   */
  handleOnPaginate: ({activePage = 1, loadData, setTableData, tableData = {}}) => {
    const { page: currentPage, loadedPages, pagination } = tableData;

    const isForwardPagination = activePage > currentPage;
    const isPageNotLoaded = loadedPages < activePage;

    if (isForwardPagination && isPageNotLoaded) {
      const query = pagination?.cursor ? { cursor: pagination.cursor } : {};
      loadData?.({ query });
      return;
    }

    setTableData?.({
      ...tableData,
      page: activePage,
    });
  },
  /**
   * Builds an Ant Design Table pagination config object from the current table state.
   * @param {Object} [options={}] - Configuration options.
   * @param {Function} options.handleOnPaginate - onChange handler for page changes.
   * @param {Object} [options.tableData={}] - Current table state with page, pagination, loadedPages, and rows.
   * @param {number} [options.perPagePagination=DEFAULT_PAGE_SIZE] - Number of rows per page.
   * @returns {{current: number, disabled: boolean, onChange: Function, pageSize: number, showSizeChanger: boolean, total: number}} Ant Design pagination config.
   * @example
   * const pagination = tableHelpers.getTablePagination({ handleOnPaginate, tableData, perPagePagination: 20 });
   */
  getTablePagination: ({
    handleOnPaginate,
    tableData = {},
    perPagePagination = DEFAULT_PAGE_SIZE
  } = {}) => {
    const {
      page = 1,
      pagination,
      loadedPages = 1,
      rows = []
    } = tableData;

    const hasCursorPagination = Boolean(pagination?.cursor);
    const totalRows = hasCursorPagination
      ? loadedPages * perPagePagination + 1
      : rows.length;

    return {
      current: page,
      disabled: rows.length === 0,
      onChange: handleOnPaginate,
      pageSize: perPagePagination,
      showSizeChanger: false,
      total: totalRows
    };
  },
  /**
   * Builds props for a "Load More" button based on cursor-based pagination state.
   * @param {Object} [options={}] - Options object.
   * @param {Object} options.tableData - Current table state; may contain a view-specific or top-level pagination cursor.
   * @param {string} options.view - Key for the active view slice within tableData.
   * @param {Function} options.loadData - Callback to load more data; called with view and cursor query.
   * @returns {{cursor: string|undefined, isEnabled: boolean, onClick: Function}} Load more props.
   * @example
   * const loadMoreProps = tableHelpers.getLoadMoreProps({ tableData, view: 'active', loadData });
   */
  getLoadMoreProps: ({tableData, view, loadData} = {}) => {
    const cursor = tableData[view]?.pagination?.cursor || tableData?.pagination?.cursor;
    return {
      cursor: cursor,
      isEnabled: Boolean(cursor),
      onClick: () => loadData({view, query: { cursor }})
    }
  },
  /**
   * Formats a single filter field's value into both a display value and an API-ready formatted value.
   * Handles 'date_range' keys specially by splitting into start/end date fields.
   * @param {string} key - Filter field key (e.g. 'date_range', 'status').
   * @param {*} value - Filter field value; for date_range, an array of two moment objects.
   * @param {Object} [options={}] - Optional configuration.
   * @param {string} [options.dateFormat='YYYY-MM-DD'] - Format string for date values.
   * @param {{start?: string, end?: string, gte?: string, lte?: string}} [options.dateKeys] - Custom keys for date fields.
   * @returns {{updatedValues: Object, formattedValues: Object}} Both raw and formatted representations of the value.
   * @example
   * tableHelpers.formatFilterPayload('date_range', [moment('2024-01-01'), moment('2024-01-31')]);
   * // { updatedValues: { start_date: ..., end_date: ... }, formattedValues: { date_gte: '2024-01-01', date_lte: '2024-01-31' } }
   */
  formatFilterPayload: (key, value, options = {}) => {
    const dateFormat = options.dateFormat || 'YYYY-MM-DD';
    if (key === 'date_range') {
      const start = value?.[0] ?? null;
      const end = value?.[1] ?? null;
      const formattedStart = start ? start.format(dateFormat) : null;
      const formattedEnd = end ? end.format(dateFormat) : null;

      const dateKeys = {
        start: options.dateKeys?.start || 'start_date',
        end: options.dateKeys?.end || 'end_date',
        gte: options.dateKeys?.gte || 'date_gte',
        lte: options.dateKeys?.lte || 'date_lte'
      };

      return {
        updatedValues: { [dateKeys.start]: start, [dateKeys.end]: end },
        formattedValues: { [dateKeys.gte]: formattedStart, [dateKeys.lte]: formattedEnd }
      };
    }

    return {
      updatedValues: { [key]: value },
      formattedValues: { [key]: value }
    };
  },
  /**
   * Builds a complete API query object from table filter state and a required date range.
   * Returns undefined if the date range is invalid.
   * @param {Object} [options={}] - Configuration options.
   * @param {Object} [options.filter={}] - Current filter field values; empty/null values are excluded.
   * @param {Object} [options.data={}] - Form data object; must contain a date_range array of two moment objects.
   * @param {Object} [options.exportAdditionalFields={}] - Additional fields to include; values are used as fallback when filter value is empty.
   * @param {{gte?: string, lte?: string}} [options.dateKeys={gte: 'start_date', lte: 'end_date'}] - API keys for start/end date bounds.
   * @returns {Object|undefined} API query object, or undefined if date_range is missing or invalid.
   * @example
   * tableHelpers.formatFilterQuery({ filter: { status: 'active' }, data: { date_range: [moment('2024-01-01'), moment('2024-01-31')] } });
   */
  formatFilterQuery: ({filter = {}, data = {}, exportAdditionalFields = {}, dateKeys = { gte: 'start_date', lte: 'end_date' } } = {}) => {
    const {date_range: dateRange} = data;
    const exportTimeFormat = 'YYYY-MM-DD';

    const isValidDateRange = dateRange && dateRange.length === 2;

    if (!isValidDateRange) return;

    const startDate = dateRange[0].format(exportTimeFormat);
    const endDate = dateRange[1].format(exportTimeFormat);

    const filterFields = Object.fromEntries(
      Object.entries(filter).filter(([_, value]) => value !== undefined && value !== null && value !== '')
    );

    const tableAdditionalFields = Object.fromEntries(
      Object.entries(exportAdditionalFields).filter(([key]) => !(key in filterFields))
    );

    const query = {
      [dateKeys.gte]: startDate,
      [dateKeys.lte]: endDate,
      ...tableAdditionalFields,
      ...filterFields,
    };

    // if exportAdditionalFields has key and filterFields has the same key but empty or [], use the value from exportAdditionalFields
    Object.entries(exportAdditionalFields).forEach(([key, value]) => {
      if (key in filterFields) {
        const filterValue = filterFields[key];
        const isEmptyArray = Array.isArray(filterValue) && filterValue.length === 0;
        const isEmptyString = typeof filterValue === 'string' && filterValue.trim() === '';
        if (isEmptyArray || isEmptyString) {
          query[key] = value;
        }
      }
    });
    return query;
  },
  // formatFilterQuery: ({filter = {}, data = {}, exportAdditionalFields = {}, dateKeys = { start: 'start_date', end: 'end_date', gte: 'date_gte', lte: 'date_lte' } } = {}) => {
  //   const {date_range: dateRange} = data;
  //   const exportTimeFormat = 'YYYY-MM-DD';
  //
  //   const isValidDateRange = dateRange && dateRange.length === 2;
  //
  //   if (!isValidDateRange) return;
  //
  //   const startDate = dateRange[0].format(exportTimeFormat);
  //   const endDate = dateRange[1].format(exportTimeFormat);
  //
  //   const filterFields = Object.fromEntries(
  //     Object.entries(filter).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  //   );
  //
  //   const tableAdditionalFields = Object.fromEntries(
  //     Object.entries(exportAdditionalFields).filter(([key]) => !(key in filterFields))
  //   );
  //
  //   const query = {
  //     [dateKeys.start]: startDate,
  //     [dateKeys.end]: endDate,
  //     ...tableAdditionalFields,
  //     ...filterFields,
  //   };
  /**
   * Generates a filename for data exports, appending the date range if available.
   * @param {Object} [options={}] - Options object.
   * @param {string} [options.baseName] - Base filename without date suffix.
   * @param {Object} [options.data={}] - Data object that may contain a date_range array of two moment objects.
   * @returns {string} Export filename string, e.g. 'expenses-2024-01-01-2024-01-31'.
   * @example
   * tableHelpers.getExportFileName({ baseName: 'expenses', data: { date_range: [moment('2024-01-01'), moment('2024-01-31')] } });
   * // 'expenses-2024-01-01-2024-01-31'
   */
  getExportFileName: ({baseName, data} = {}) => {
    const dateRange = data?.date_range;
    if (!dateRange || dateRange.length !== 2) return baseName || 'export';
    const exportTimeFormat = 'YYYY-MM-DD';

    const startDate = dateRange[0].format(exportTimeFormat);
    const endDate = dateRange[1].format(exportTimeFormat);

    return baseName ? `${baseName}-${startDate}-${endDate}` : `${startDate}-${endDate}`;
  },
  /**
   * Renders a user avatar with a tooltip showing the user's full name or email.
   * @param {Object} options - Avatar options.
   * @param {Object|null} options.user - User object with optional full_name, email, and logo properties.
   * @param {string|number} options.key - React key for the wrapping element.
   * @param {...*} rest - Additional props spread onto the wrapper div.
   * @returns {React.ReactElement|null} Avatar element wrapped in a Tooltip, or null if user is falsy.
   * @example
   * tableHelpers.getUserAvatar({ user: { full_name: 'Jane Doe', logo: '/avatar.png' }, key: 'user-1' });
   */
  getUserAvatar: ({ user, key, ...rest }) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <div key={key} {...rest}>
        <Tooltip title={name}>
          <Avatar src={user?.logo} size={24} style={{fontSize: 12}}>
            {textHelpers.getInitials(name)}
          </Avatar>
        </Tooltip>
      </div>
    );
  },
  /**
   * Returns a Space element containing an Avatar and the user's full name or email.
   * @param {Object|null} user - User object with full_name, email, and optional logo.
   * @returns {React.ReactElement|null} Space with Avatar and name, or null if no user.
   * @example
   * tableHelpers.getUserAvatarWithName({ full_name: 'Jane Doe', email: 'jane@example.com' });
   */
  getUserAvatarWithName: ({employee, spaceProps = {}, avatarProps = {}} = {}) => {
    if (!employee) return null;
    const name = employee?.full_name || employee?.email;
    return (
      <Space className='d-flex-child' size='small' {...spaceProps}>
        <Avatar src={employee?.logo} size={24} style={{fontSize: 12}} {...avatarProps}>
          {textHelpers.getInitials(name)}
        </Avatar>
        <span>{name}</span>
      </Space>
    )
  },
}
